import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useEdgeUnitStatusInfo,
  useSolutionType,
  useSettingsUI,
  useSettingsData,
  useSystemId,
} from "../../hooks/useReduxSelectors";
import { useDispatch } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Stack,
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchReports } from "../../ducks/reportsDuck";
import { getReportUrl } from "../../constants/apiUrl";
import { fetchSettingsAuth } from "../../ducks/settingsDuck";
import { SETTING_TABLE_NAME } from "Constants/settingsTableName";

const nameMapping = {
  "HostUsedCpuPercentQuantile0.9": "CPU",
  "HostUptimeSeconds": "Uptime",
  "UsedMemoryBytesProsentage": "RAM",
  "FreeMemoryGB": "Free RAM",
  "HostUsedMemoryBytes": "Used RAM",
  "DiskUseLevel": "Disk Usage",
  "FreeDiskSpaceGB": "Total Free Disk",
  "IothubSyncsTotal": "Iothub Syncs",
  "HostTotalMemoryBytes": "Total RAM",
};

const getMappedName = (key) => nameMapping[key] || key;

// Primary metrics shown as mini gauges
const PRIMARY_METRICS = [
  "HostUsedCpuPercentQuantile0.9",
  "UsedMemoryBytesProsentage",
  "DiskUseLevel",
];

const getBarColor = (value) => {
  if (value > 90) return "error";
  if (value > 75) return "warning";
  return "primary";
};

// Mini metric bar component
const MetricBar = ({ label, value, unit, showBar }) => {
  const numVal = typeof value === "number" ? value : 0;
  const isPercent = unit === "%" || unit === "%25";
  const hasBar = showBar || isPercent;
  return (
    <Box sx={{ minWidth: 90, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
          {numVal.toFixed(1)}{isPercent ? "%" : ` ${unit || ""}`}
        </Typography>
      </Stack>
      {hasBar && (
        <LinearProgress
          variant="determinate"
          value={Math.min(numVal, 100)}
          color={getBarColor(numVal)}
          sx={{ height: 3, borderRadius: 1, bgcolor: "action.hover" }}
        />
      )}
    </Box>
  );
};

// Routine status card (AutoZero / QAL3)
const RoutineCard = ({ label, data, isFailed, isRunning }) => (
  <Box sx={{
    flex: 1,
    minWidth: 120,
    bgcolor: "background.dark",
    borderRadius: 1,
    px: 1.5,
    py: 1,
    borderLeft: "3px solid",
    borderColor: isFailed ? "error.main" : "primary.main",
  }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography sx={{ textTransform: "uppercase", fontWeight: 600 }}>
        {label}
      </Typography>
      {isRunning && <CircularProgress size={12} sx={{ color: "secondary.main" }} />}
    </Stack>
    <Chip
      label={data.status || "—"}
      size="small"
      color={isFailed ? "error" : "success"}
      variant="outlined"
    />
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <Box sx={{ display: "flex", spacing: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Last:
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {data.lastRun || "—"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", spacing: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Next:
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {data.nextRun || "—"}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const EdgeUnitStatusPanel = React.memo(() => {
  const systemId = useSystemId();
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  const edgeUnitInfoPanelData = useEdgeUnitStatusInfo();
  const solutionType = useSolutionType();
  const settingsUI = useSettingsUI();
  const [showHwInfo, setShowHwInfo] = useState(false);
  const autozeroRunning = settingsUI?.autoZero?.isRunManuallyBtnDisabled;
  const qal3Running = settingsUI?.qal3?.isRunManuallyBtnDisabled;
  const settings = useSettingsData();
  const systemName = settings?.GeneralSettings?.formattedEntries?.jsonData?.systemName;
  const runActive = edgeUnitInfoPanelData?.runActive;
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    if (systemId === "edge") {
      const updateTime = () => {
        const now = new Date();
        setCurrentTime(
          `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`
        );
      };
      updateTime();
      const interval = setInterval(updateTime, 5000);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    if (runActive === false) {
      dispatch(fetchReports({ systemId, url: getReportUrl, type: "QAL3", args: { ReportName: "QAL3", Count: 1 } }));
      dispatch(fetchReports({ systemId, url: getReportUrl, type: "AutoZero", args: { ReportName: "AutoZero", Count: 1 } }));
      dispatch(fetchSettingsAuth({ systemId, args: { setting: SETTING_TABLE_NAME.AUTOZERO_SETTINGS, stateName: "AutoZeroSettings" } }));
      dispatch(fetchSettingsAuth({ systemId, args: { setting: SETTING_TABLE_NAME.QAL3_SETTINGS, stateName: "QAL3Settings" } }));
    }
  }, [runActive]);

  // Data availability checks
  let hasAutoZero = false, hasQAL3 = false, hasCPU = false;
  let autoZeroFailed = false, qal3Failed = false;

  if (edgeUnitInfoPanelData) {
    hasAutoZero = Object.values(edgeUnitInfoPanelData.autoZero).some((v) => v !== "");
    hasQAL3 = Object.values(edgeUnitInfoPanelData.QAL3).some((v) => v !== "");
    hasCPU = Object.values(edgeUnitInfoPanelData.CPU).some((v) => v !== "");
    autoZeroFailed = edgeUnitInfoPanelData.autoZero.status?.toLowerCase().includes("failed");
    qal3Failed = edgeUnitInfoPanelData.QAL3.status?.toLowerCase().includes("failed");
  }

  const hasNoData = !hasAutoZero && !hasQAL3 && !hasCPU;
  if (solutionType === "lora" || hasNoData) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 0.75, bgcolor: "background.dark", borderRadius: 1, mb: 0.5 }}>
        {systemName && <Typography variant="body2" sx={{ fontWeight: 500 }}>{systemName}</Typography>}
        {systemId === "edge" && <Typography variant="body2" sx={{ color: "text.secondary" }}>{currentTime}</Typography>}
      </Box>
    );
  }
  console.log("edgeUnitInfoPanelData", edgeUnitInfoPanelData)

  const cpu = edgeUnitInfoPanelData?.CPU || {};
  const extraMetrics = Object.entries(cpu).filter(([key]) => !PRIMARY_METRICS.includes(key));
  console.log("extraMetrics", extraMetrics)

  return (
    <Box sx={{ bgcolor: "background.dark", borderRadius: 1, mb: 0.5 }}>
      {/* Header row: system info + primary HW metrics */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 0.75, flexWrap: "wrap" }}
      >
        {/* System name & time */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: "auto" }}>
          {systemName && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{systemName}</Typography>
          )}
          {systemId === "edge" && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{currentTime}</Typography>
          )}
        </Stack>

        {/* Primary HW metric bars — inline in header */}
        {hasCPU && (
          <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
            {PRIMARY_METRICS.map((key) => {
              const entry = cpu[key];
              if (!entry) return null;
              return (
                <MetricBar
                  key={key}
                  label={getMappedName(key)}
                  value={entry.Value}
                  unit={entry.Unit}
                  showBar
                />
              );
            })}
          </Stack>
        )}

        {/* HW expand toggle */}
        {hasCPU && extraMetrics.length > 0 && (
          <Chip
            icon={<MemoryIcon />}
            deleteIcon={
              <ExpandMoreIcon
                sx={{
                  transition: "transform 0.2s",
                  transform: showHwInfo ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            }
            onDelete={() => setShowHwInfo((p) => !p)}
            onClick={() => setShowHwInfo((p) => !p)}
            label={t("HW Info")}
            variant={showHwInfo ? "filled" : "outlined"}
          />
        )}
      </Stack>

      {/* Routine status cards */}
      {(hasAutoZero || hasQAL3) && (
        <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1 }}>
          {hasAutoZero && (
            <RoutineCard
              label="AutoZero"
              data={edgeUnitInfoPanelData.autoZero}
              isFailed={autoZeroFailed}
              isRunning={autozeroRunning}
            />
          )}
          {hasQAL3 && (
            <RoutineCard
              label="QAL3"
              data={edgeUnitInfoPanelData.QAL3}
              isFailed={qal3Failed}
              isRunning={qal3Running}
            />
          )}
        </Stack>
      )}

      {/* Collapsible extra HW info */}
      <Collapse in={showHwInfo} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
          <Grid container spacing={1.5}>
            {extraMetrics.map(([key, entry]) => (
              <Grid key={key} size={{ xs: 6, sm: 4, md: 3 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block", mb: 0.25 }}>
                  {getMappedName(key)}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                  {entry?.Value % 1 === 0 ? entry?.Value : entry?.Value?.toFixed(2)} {entry?.Unit}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
});

export default EdgeUnitStatusPanel;
